"use client";

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  effects?: React.ReactNode;
}

export default function Modal({ open, onClose, children, effects }: ModalProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Simple enter animation toggle
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    } else {
      setVisible(false);
    }
  }, [open]);

  // Focus trap within modal content
  useEffect(() => {
    if (!open) return;
    const root = contentRef.current;
    if (!root) return;
    const selectors = [
      'a[href]', 'button', 'textarea', 'input', 'select', '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    const getFocusables = () => Array.from(root.querySelectorAll<HTMLElement>(selectors))
      .filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

    const focusables = getFocusables();
    (focusables[0] || root).focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const current = getFocusables();
      if (current.length === 0) return;
      const first = current[0];
      const last = current[current.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    root.addEventListener('keydown', onKeyDown as any);
    return () => root.removeEventListener('keydown', onKeyDown as any);
  }, [open]);

  if (!mounted || !open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Diálogo modal"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Effects layer: full screen, above backdrop, behind modal content */}
      {effects ? (
        <div className="fixed inset-0 pointer-events-none z-[5]">
          {effects}
        </div>
      ) : null}

      <div
        ref={contentRef}
        className={[
          'relative z-10 bg-white shadow-2xl ring-1 ring-black/5 w-full max-w-4xl',
          'rounded-xl md:rounded-2xl overflow-hidden',
          'transition-all duration-200',
          visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-1 scale-95'
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Botón de cerrar */}
        <button
          className="absolute top-3 right-3 z-10 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/90 text-black hover:bg-white shadow-lg ring-1 ring-black/5 transition"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="max-h-[85vh] overflow-auto p-4 md:p-6">
          {children ?? null}
        </div>
      </div>
    </div>,
    document.body
  );
}
